import {Patient} from './patient';
import {TaskProposal} from './task-proposal';
import {Task} from './models';
import {
    PatientForPersonSerializer,
    PatientListSerializer,
    PatientListSerializerWithPrimaryEligibility,
    PatientSimpleSerializer,
    PayerAltNameSerializer,
} from '../@core/api.service';
import {BST_CENSUS_MISMATCHES} from '../@core/bst_census_mismatches';
import {Visit} from './visit';
import {compareDates} from '../@theme/helpers';
import {getId} from '../utils/type.utils';
import {GetterCache} from '../utils/accessor.utils';
import {CensusEvent} from './census-event';
import {InsuranceEligibility} from './insurance-eligibility';

export interface CensusWarning {
    icon: string;
    level: string;
    text: string;
    key?: string;
}

/**
 * The BroadStreet census mismatches the backend can report.
 *
 * Both the keys and the text come from BST_CENSUS_MISMATCHES, exported by `manage.py
 * export_constants` from backend/apps/core/mvc/patient_census/bst_census_mismatch.py. Nothing here
 * re-derives *which* of them apply: the list filter on the patients page and these warnings have to
 * agree, and they only do while the backend owns the comparison.
 */
export type BstCensusMismatch = keyof typeof BST_CENSUS_MISMATCHES;

/** Reported on its own, because callers gate it on whether a BroadStreet record is expected at all. */
export const NO_BST_RECORD: BstCensusMismatch = 'NO_BST';

/** How each mismatch looks. The backend owns whether it applies and what it says. */
export const BST_CENSUS_WARNINGS: {[key in BstCensusMismatch]: CensusWarning} = {
    NO_BST: {icon: 'check-circle', level: 'danger', text: BST_CENSUS_MISMATCHES.NO_BST, key: 'NO_BST'},
    BST_FACILITY_MISMATCH: {icon: 'building', level: 'danger', text: BST_CENSUS_MISMATCHES.BST_FACILITY_MISMATCH, key: 'BST_FACILITY_MISMATCH'},
    BST_CENSUS_MISMATCH: {icon: 'map-marker-edit', level: 'danger', text: BST_CENSUS_MISMATCHES.BST_CENSUS_MISMATCH, key: 'BST_CENSUS_MISMATCH'},
    BST_LOC_MISSING: {icon: 'hand-holding-usd', level: 'danger', text: BST_CENSUS_MISMATCHES.BST_LOC_MISSING, key: 'BST_LOC_MISSING'},
    BST_LOC_MISMATCH: {icon: 'hand-holding-usd', level: 'danger', text: BST_CENSUS_MISMATCHES.BST_LOC_MISMATCH, key: 'BST_LOC_MISMATCH'},
};

export const filterProposalsForPatient = (patient: Partial<Patient>, taskProposals: TaskProposal[]) =>
    taskProposals.filter(tp => getId(tp.patient) === getId(patient.id) || patient.person && getId(tp.patient.person) === getId(patient.person));

export const filterTasksForPatient = (patient: Partial<Patient>, tasks: Task[]) =>
    tasks.filter(t => getId(t.patient) === getId(patient.id) || getId(t.source_patient) === getId(patient.id));

export class PatientForVisit extends Patient {
    taskProposals: TaskProposal[];
    proposalsForVisit: TaskProposal[];
    confirmedForVisit: TaskProposal;
    otherScheduled: TaskProposal[];
    tasksForVisit: Task[];

    // PatientListSerializerWithPrimaryEligibility
    last_primary_eligibility?: InsuranceEligibility;
    primary_payer?: PayerAltNameSerializer;

    private _visit: Visit;

    @GetterCache()
    get censusInfo(): PatientForPersonSerializer[] {
        if (this.personDetails) {
            return [...this.personDetails.patients].sort((a, b) =>
                new Date(b.active_census_date || null).getTime() - new Date(a.active_census_date || null).getTime() ||
                new Date(b.census_date || null).getTime() - new Date(a.census_date || null).getTime() ||
                (b.customer || 0) - (a.customer || 0)
            );
        }

        return [{
            id: this.id,
            facility: getId(this.facility),
            census_status: this.census_status,
            census_date: this.census_date,
            mrid: this.mrid,
            origin: this.origin,
            customer: getId(this.customer),
            physician_teams: this.physician_teams,
            level_of_care: this.level_of_care,
        }];
    }

    get lastRelatedCensus() {
        return this.censusInfo[0];
    }

    @GetterCache()
    get bstCensusInfo() {
        return this.censusInfo.filter(x => Patient.isBst(x));
    }

    /**
     * Which mismatches the backend found between this person's BroadStreet records and the imported
     * one. Empty when the payload carries no expanded person — the comparison needs every record for
     * the person, so without them there is nothing to report.
     */
    @GetterCache()
    get censusMismatches(): BstCensusMismatch[] {
        return (this.personDetails?.census_mismatches ?? []) as BstCensusMismatch[];
    }

    /** Reported separately from the mismatches: callers gate this one on whether a BST record is expected. */
    @GetterCache()
    get bstRecordWarning(): CensusWarning {
        return this.censusMismatches.includes(NO_BST_RECORD) ? BST_CENSUS_WARNINGS[NO_BST_RECORD] : null;
    }

    @GetterCache()
    get bstCensusWarnings(): CensusWarning[] {
        return this.censusMismatches
            .filter(mismatch => mismatch !== NO_BST_RECORD)
            .map(mismatch => BST_CENSUS_WARNINGS[mismatch])
            .filter(Boolean);
    }

    /**
     * Stays on the client: these depend on the visit being composed, which the backend has no
     * knowledge of when it serializes the patient.
     */
    @GetterCache()
    get visitCensusWarnings(): CensusWarning[] {
        const warnings: CensusWarning[] = [];
        if (this._visit?.physicianTeam) {
            const arsanaCensus = this.censusInfo.find(x => getId(x.facility) === getId(this._visit.physicianTeam.facility));
            if (arsanaCensus?.physician_teams.every(x => x !== getId(this._visit.physicianTeam))) {
                warnings.push({
                    icon: 'stethoscope',
                    level: 'danger',
                    text: 'Arsana record not assigned to the current Physician Team',
                    key: 'ARSANA_NOT_ASSIGNED',
                });
            }
            if (this._visit.physicianTeam.customer) {
                const bstCensus = this.bstCensusInfo.find(x => getId(x.customer) === getId(this._visit.physicianTeam.customer));
                if (!bstCensus) {
                    warnings.push({
                        icon: 'check-circle',
                        level: 'warning',
                        text: BST_CENSUS_MISMATCHES.NO_BST,
                        key: NO_BST_RECORD,
                    });
                } else if (bstCensus.physician_teams.every(x => x !== getId(this._visit.physicianTeam))) {
                    warnings.push({
                        icon: 'stethoscope',
                        level: 'warning',
                        text: 'BroadStreet record not assigned to the current Physician Team',
                        key: 'BST_NOT_ASSIGNED',
                    });
                }
            }
        }
        return warnings;
    }

    @GetterCache()
    get occupancyCensusWarnings(): CensusWarning[] {
        const warnings: CensusWarning[] = [];
        const lastOccupancyStatus = CensusEvent.occupancyStatus(this.lastRelatedCensus.census_status);
        if (CensusEvent.hasStatusWarning(lastOccupancyStatus)) {
            warnings.push({
                icon: 'exclamation-triangle',
                level: CensusEvent.colorClass(lastOccupancyStatus),
                text: CensusEvent.statusText(this.lastRelatedCensus.census_status),
            });
        }
        return warnings;
    }

    @GetterCache()
    get censusWarnings(): CensusWarning[] {
        return [...this.bstCensusWarnings, ...this.visitCensusWarnings, ...this.occupancyCensusWarnings];
    }

    constructor(patient: PatientListSerializer | PatientForVisit | PatientSimpleSerializer | PatientListSerializerWithPrimaryEligibility, taskProposals?: TaskProposal[], visit?: Visit, tasks?: Task[], desc?: boolean) {
        super(patient);

        this._visit = visit;
        if (taskProposals) this.setProposals(taskProposals, visit, tasks, desc);
        if ((patient as PatientListSerializerWithPrimaryEligibility)?.last_primary_eligibility) {
            this.last_primary_eligibility = new InsuranceEligibility((patient as PatientListSerializerWithPrimaryEligibility)?.last_primary_eligibility);
        }
    }

    setProposals(taskProposals: TaskProposal[], visit?: Visit, tasks?: Task[], desc = false) {
        this.taskProposals = filterProposalsForPatient(this, taskProposals).sort((a, b) =>
            (a.visit ? 0 : 1) - (b.visit ? 0 : 1) ||
            compareDates(a.visit ? a.visit.date : new Date(), b.visit ? b.visit.date : new Date(), desc));

        if (visit) {
            this.proposalsForVisit = this.getProposalsFor(visit, true);
            this.confirmedForVisit = this.proposalsForVisit.find(x => x.confirmed && getId(x.visit) == getId(visit));
            this.otherScheduled = this.taskProposals.filter(x => x.confirmed && x.visit && !this.proposalsForVisit.includes(x));
            this.tasksForVisit = filterTasksForPatient(this, tasks);
        }
    }

    getProposalsFor(visit: Visit, includeVisitless = false) {
        return this.taskProposals.filter(x => getId(x.visit) == getId(visit) || includeVisitless && !x.visit && visit.isType(x.visit_type));
    }

    hasProposalFor(visit: Visit, includeVisitless = false) {
        return this.taskProposals.some(x => getId(x.visit) == getId(visit) || includeVisitless && !x.visit && visit.isType(x.visit_type));
    }

    isConfirmedFor(visit: Visit | number) {
        return this.taskProposals.some(x => x.confirmed && getId(x.visit) == getId(visit));
    }
}
